import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Segmented,
  Sheet,
  Spinner,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { nextApi, type AutomationDto, type AutomationTemplateDto } from "../../api/nextApi";
import { EVENTS, notifyAutomationsChanged, onEvent } from "../events";
import { useShell } from "../shell/ShellContext";
import { AutomationCard, TemplateCard, useAutomationActions } from "./AutomationParts";
import { AutomationEditor, type AutomationDraft } from "./AutomationEditor";
import { AutomationAiChat, useAutomationAiAvailable } from "./AutomationAiChat";
import { loadCatalog, screenKeyForPath, useAutomationsState } from "./automationsStore";
import "./automations.css";

export const AUTOMATIONS_PATH = "/automations";

/** lightning bolt — the automations glyph */
export function BoltIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2 4.5 13.5H12L11 22l8.5-11.5H12L13 2z" />
    </svg>
  );
}

export const templateToDraft = (t: AutomationTemplateDto): AutomationDraft => ({
  name: t.name,
  description: t.description,
  screenKey: t.screen,
  templateKey: t.key,
  trigger: t.trigger,
  conditions: t.conditions,
  actions: t.actions,
});

export const automationToDraft = (a: AutomationDto): AutomationDraft => ({
  id: a.id,
  name: a.name,
  description: a.description,
  screenKey: a.screenKey,
  templateKey: a.templateKey,
  status: a.status === "paused" ? "paused" : "active",
  trigger: a.trigger,
  conditions: a.conditions,
  actions: a.actions,
});

/** "אוטומציות" in the top bar — shows where the user may see automations; badge = active ones on this screen. */
export function AutomationsButton() {
  const { can } = useShell();
  const location = useLocation();
  const visible = can("automations");
  const snap = useAutomationsState(visible);
  if (!visible) return null;
  const screenKey = screenKeyForPath(location.pathname, snap.catalog?.screens);
  const count = Number(snap.counts?.[screenKey]) || 0;
  const label = count ? `אוטומציות בדף זה — ${count} פעילות` : "אוטומציות בדף זה";
  return (
    <button
      type="button"
      className="atm-btn"
      onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.automations))}
      aria-label={label}
      title={snap.errors ? `${label} · יש אוטומציה שנכשלה` : label}
    >
      <BoltIcon />
      <span className="atm-btn__label">אוטומציות</span>
      {count ? <span className="atm-btn__badge">{count > 99 ? "99+" : count}</span> : null}
      {snap.errors ? <span className="atm-btn__dot" aria-hidden="true" /> : null}
    </button>
  );
}

/**
 * AutomationsPanel — "אוטומציות בדף זה": a side sheet with the automations working on the current screen,
 * ready-made ones to turn on in one click, and a new one from scratch. Mounted once by the shell.
 */
export function AutomationsPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useCkToast();
  const { can, allowed, current } = useShell();
  const visible = can("automations");
  const canManage = allowed("automations.manage");
  const snap = useAutomationsState(visible);
  const [open, setOpen] = useState(false);
  const aiAvailable = useAutomationAiAvailable();
  const [tab, setTab] = useState<"here" | "available" | "ai">("here");
  const [aiPrompt, setAiPrompt] = useState<string | null>(null);
  const [aiKey, setAiKey] = useState(0);
  const [items, setItems] = useState<AutomationDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AutomationDraft | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const catalog = snap.catalog;
  const screenKey = screenKeyForPath(location.pathname, catalog?.screens);
  const screenLabel = catalog?.screens.find((s) => s.key === screenKey)?.label ?? current?.label ?? "";

  const reload = useCallback(() => {
    setError(null);
    nextApi.automations
      .list()
      .then(setItems)
      .catch((e: unknown) => setError((e as { message?: string })?.message || "לא הצלחנו לטעון את האוטומציות"));
  }, []);

  useEffect(
    () =>
      onEvent<{ tab?: string; prompt?: string | null }>(EVENTS.automations, (d) => {
        setOpen(true);
        setTab(d?.tab === "available" ? "available" : d?.tab === "ai" ? "ai" : "here");
        if (d?.tab === "ai") {
          setAiPrompt(d.prompt ?? null);
          if (d.prompt) setAiKey((k) => k + 1);
        }
        void loadCatalog();
        reload();
      }),
    [reload],
  );
  useEffect(() => setOpen(false), [location.pathname]);

  const actions = useAutomationActions(reload);
  const here = useMemo(() => (items ?? []).filter((a) => a.screenKey === screenKey), [items, screenKey]);
  const usedTemplates = useMemo(() => new Set((items ?? []).map((a) => a.templateKey).filter(Boolean)), [items]);
  const templates = useMemo(
    () => (catalog?.templates ?? []).filter((t) => t.screen === screenKey && !usedTemplates.has(t.key)),
    [catalog, screenKey, usedTemplates],
  );

  const addTemplate = async (t: AutomationTemplateDto) => {
    setAdding(t.key);
    try {
      await nextApi.automations.fromTemplate(t.key);
      toast.success(`„${t.name}” הופעלה`);
      notifyAutomationsChanged();
      reload();
      setTab("here");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "ההפעלה נכשלה");
    } finally {
      setAdding(null);
    }
  };

  if (!visible) return null;

  return (
    <>
      <Sheet open={open} onClose={() => setOpen(false)} width={560} ariaLabel="אוטומציות בדף זה" className="atm">
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <header className="atm__head">
            <span className="atm__logo">
              <BoltIcon size={20} />
            </span>
            <div className="atm__titles">
              <b>אוטומציות בדף זה</b>
              <small>{screenLabel ? `«${screenLabel}» · מה קורה כאן לבד` : "מה קורה לבד במערכת"}</small>
            </div>
            <IconButton icon="refresh" label="רענון" onClick={reload} />
            <IconButton icon="x" label="סגירה" onClick={() => setOpen(false)} />
          </header>
          <div className="atm__tabs">
            <Segmented
              ariaLabel="תצוגה"
              value={tab}
              onChange={(v) => setTab(v as "here")}
              items={[
                { value: "here", label: "פועלות כאן", count: here.length },
                { value: "available", label: "מוכנות להפעלה", count: templates.length },
                ...(aiAvailable && canManage ? [{ value: "ai", label: "יצירה עם AI" }] : []),
              ]}
            />
          </div>
          {aiAvailable && canManage ? (
            <div className="atm-ai-wrap" hidden={tab !== "ai"}>
              <AutomationAiChat
                key={aiKey}
                screenKey={screenKey}
                catalog={catalog}
                initialPrompt={aiPrompt}
                onEdit={(d) => setDraft(d)}
                onOpenAutomation={(id) => {
                  const a = (items ?? []).find((x) => x.id === id);
                  if (a) setDraft(automationToDraft(a));
                }}
                onCreated={reload}
              />
            </div>
          ) : null}
          <div className="atm__body" hidden={tab === "ai"}>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            {!items || !catalog ? (
              <div className="atm-busy">
                <Spinner size={24} />
              </div>
            ) : tab === "here" ? (
              here.length ? (
                <div className="atm-list">
                  {here.map((a) => (
                    <AutomationCard
                      key={a.id}
                      a={a}
                      canManage={canManage}
                      busy={actions.busyId === a.id}
                      onToggle={actions.toggle}
                      onRun={actions.run}
                      onEdit={(x) => setDraft(automationToDraft(x))}
                      onHistory={actions.showHistory}
                      onDelete={actions.askDelete}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="sparkle"
                  title="אין עדיין אוטומציות במסך הזה"
                  action={
                    templates.length ? (
                      <Button variant="primary" onClick={() => setTab("available")}>
                        לאוטומציות מוכנות ({templates.length})
                      </Button>
                    ) : null
                  }
                >
                  אוטומציה עושה משהו לבד — שולחת מייל, פותחת משימה או שולחת דוח — לפי לוח זמנים או כשמשהו קורה.
                </EmptyState>
              )
            ) : templates.length ? (
              <div className="atm-list">
                {templates.map((t) => (
                  <TemplateCard
                    key={t.key}
                    t={t}
                    canManage={canManage}
                    busy={adding === t.key}
                    onAdd={() => void addTemplate(t)}
                    onCustomize={() => setDraft(templateToDraft(t))}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon="check" title="כל האוטומציות המוכנות למסך הזה כבר פעילות">
                אפשר ליצור אוטומציה משלכם — „אוטומציה חדשה” למטה.
              </EmptyState>
            )}
          </div>
          <footer className="atm__foot">
            <Button
              size="sm"
              variant="ghost"
              icon="list"
              onClick={() => {
                setOpen(false);
                navigate(AUTOMATIONS_PATH);
              }}
            >
              כל האוטומציות
            </Button>
            {canManage ? (
              <Button size="sm" variant="primary" icon="plus" onClick={() => setDraft({ screenKey })}>
                אוטומציה חדשה
              </Button>
            ) : (
              <small>
                <Icon name="key" size={12} /> צפייה בלבד
              </small>
            )}
          </footer>
        </div>
      </Sheet>
      <AutomationEditor open={Boolean(draft)} draft={draft} catalog={catalog} onClose={() => setDraft(null)} onSaved={() => reload()} />
      {actions.dialogs}
    </>
  );
}

export default AutomationsPanel;
